import React from "react";
import { Link } from "react-router-dom";
import { imageUrl } from "@/lib/api";
import { CheckCircle2, AlertTriangle, XCircle, Sparkles } from "lucide-react";

const conditionColor = {
  "New": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Like New": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Excellent": "bg-blue-50 text-blue-700 border-blue-200",
  "Very Good": "bg-blue-50 text-blue-700 border-blue-200",
  "Good": "bg-slate-100 text-slate-700 border-slate-200",
  "Fair": "bg-amber-50 text-amber-700 border-amber-200",
  "For Parts / Repair": "bg-rose-50 text-rose-700 border-rose-200",
};

function StatusPill({ status }) {
  if (status === "available") return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700">
      <CheckCircle2 className="w-3.5 h-3.5" /> In stock
    </span>
  );
  if (status === "low_stock") return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700">
      <AlertTriangle className="w-3.5 h-3.5" /> Low stock
    </span>
  );
  if (status === "out_of_stock") return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-rose-700">
      <XCircle className="w-3.5 h-3.5" /> Out of stock
    </span>
  );
  if (status === "sold") return <span className="text-xs font-medium text-slate-500">Sold</span>;
  return <span className="text-xs font-medium text-slate-500">{status}</span>;
}

export default function ProductCard({ product }) {
  const img = product.images && product.images[0];
  const keySpecs = [];
  if (product.specs?.Processor) keySpecs.push(product.specs.Processor);
  if (product.specs?.RAM) keySpecs.push(product.specs.RAM);
  if (product.specs?.Storage) keySpecs.push(product.specs.Storage);

  return (
    <Link
      to={`/product/${product.id}`}
      data-testid={`product-card-${product.sku}`}
      className="card-lift group relative flex flex-col bg-white border border-slate-200 rounded-2xl overflow-hidden"
    >
      <div className="aspect-[4/3] bg-slate-50 overflow-hidden border-b border-slate-200 relative">
        {img ? (
          <img
            src={imageUrl(img)}
            alt={product.name}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full grid place-items-center text-slate-300 text-xs">No image</div>
        )}
        {product.featured && (
          <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-900 text-white text-[10px] font-semibold tracking-wider uppercase">
            <Sparkles className="w-3 h-3" /> Featured
          </span>
        )}
        <span className={`absolute top-3 right-3 px-2 py-0.5 rounded-full text-[11px] font-medium border ${conditionColor[product.condition] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
          {product.condition}
        </span>
      </div>
      <div className="p-4 sm:p-5 flex flex-col gap-2 flex-1">
        <div className="text-xs font-medium text-slate-500 tracking-wide uppercase">{product.brand}</div>
        <h3 className="font-display text-base sm:text-lg font-semibold text-slate-900 leading-snug line-clamp-2">
          {product.name}
        </h3>
        {keySpecs.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-1">
            {keySpecs.slice(0, 3).map((s, i) => (
              <span key={i} className="font-mono text-[10px] text-slate-600 bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5">
                {s}
              </span>
            ))}
          </div>
        )}
        <div className="mt-auto pt-3 flex items-end justify-between">
          <div>
            <div className="font-display text-xl font-bold text-slate-900">${product.price?.toFixed(2)}</div>
            <StatusPill status={product.status} />
          </div>
          <span className="text-xs font-medium text-blue-600 group-hover:underline">View details →</span>
        </div>
      </div>
    </Link>
  );
}
